/*
  EPD US V3, Sprint A, mobile UX. 19 Aug 2026.

  Three behaviour fixes for the homepage part finder, applied from outside
  assets/parts-finder.js rather than by editing it.

  Why: parts-finder.js is 20 KB and partly minified, Shopify's Admin API has no
  patch operation, and the part finder is the store's primary conversion tool.
  Resending the whole file by hand to change two lines is the riskier option.
  Each fix below is deliberately narrow and reversible.

  Fold these into parts-finder.js when someone has the theme checked out locally
  with Shopify CLI, then delete this file and its line in perf-hints.liquid.

  Loaded with `defer`, so it runs after parts-finder.js has registered its own
  listeners.
*/
(function () {
  'use strict';

  var SEARCH_INPUT = '#partfinder-search';
  var SEARCH_BUTTON = '.part-finder-section .search-button';

  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  /*
    FIX 1: part-number-safe input attributes.

    The field had no autocapitalize, autocorrect, spellcheck or inputmode, so on
    iOS the first character was capitalised and alphanumeric strings were
    autocorrected: "e320d" reached Relewise as "E320d" or as a dictionary word.
    enterkeyhint gives the keyboard a "Search" key instead of a generic return.

    `type` is deliberately NOT changed to "search": that would add a second,
    native clear button next to the theme's own #clear-search-icon.
  */
  function upgradeInput() {
    var input = document.querySelector(SEARCH_INPUT);
    if (!input) return;

    input.setAttribute('inputmode', 'text');
    input.setAttribute('enterkeyhint', 'search');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');

    if (!input.getAttribute('aria-label')) {
      input.setAttribute('aria-label', 'Search part type, OEM or Berco part number');
    }

    // The original placeholder truncated at 390px.
    input.setAttribute('placeholder', 'Search part type or part number');
  }

  /*
    FIX 2: the return key must run the same path as the Search button.

    parts-finder.js binds Enter to redirectToSearchPage(), which navigates to
    /search?rw-term=... and drops QUERY_VARIABLES entirely, so the manufacturer
    and model the customer just chose are silently discarded. handleSearchClick()
    (the Search button) appends brand= and model=.

    Dismissing the keyboard with the return key is the default behaviour on a
    phone, so this was likely the majority path.

    Capture phase on document runs before the input's own listener, and
    stopImmediatePropagation prevents it, so the button's handler is the only one
    that runs.
  */
  function fixEnterKey() {
    document.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'Enter') return;

        var input = event.target;
        if (!input || typeof input.matches !== 'function') return;
        if (!input.matches(SEARCH_INPUT)) return;

        var button = document.querySelector(SEARCH_BUTTON);
        if (!button) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        button.click();
      },
      true
    );
  }

  /*
    FIX 3: keep the dropdown's scroll position when an option is ticked.

    Every checkbox change runs updateState() -> renderBrandFilterOptions(), which
    does dropdownListEl.innerHTML = "" and re-inserts all 150 <li> elements.
    Emptying the <ul> collapses the panel's scroll height, so the browser clamps
    scrollTop to 0 and the customer is thrown from "Volvo" back to "Aces".

    The panel element itself survives the re-render (only the <ul>'s children are
    replaced), so its scrollTop can be recorded in the capture phase and restored
    on the next frame.
  */
  function preserveDropdownScroll() {
    var panel = null;
    var saved = null;

    document.addEventListener(
      'change',
      function (event) {
        var target = event.target;
        if (!target || typeof target.closest !== 'function') return;

        panel = target.closest('.dropdown-filter__options');
        saved = panel ? panel.scrollTop : null;
      },
      true
    );

    document.addEventListener(
      'change',
      function () {
        if (panel === null || saved === null) return;

        var thisPanel = panel;
        var thisTop = saved;
        panel = null;
        saved = null;

        window.requestAnimationFrame(function () {
          if (thisPanel && thisPanel.scrollTop !== thisTop) {
            thisPanel.scrollTop = thisTop;
          }
        });
      },
      false
    );
  }

  ready(function () {
    upgradeInput();
    fixEnterKey();
    preserveDropdownScroll();
  });
})();